import React, { useState, useEffect } from 'react';
import AdminUsersTab from './AdminUsersTab';
import TransactionsPage from './TransactionsPage';
import AdminConfigTab from './AdminConfigTab';
import { T } from './theme';
import { fetchAllDonations, fetchAllExpenses } from '../services/donationService';
import { useRefresh } from './RefreshContext';

const API_BASE = 'https://d3bdqxkpn753kf.cloudfront.net';

const TABS = [
  { id: 'users',        label: 'Users',     icon: '👥', desc: 'Manage members & roles' },
  { id: 'transactions', label: 'Donations', icon: '💰', desc: 'Review & approve donations' },
  { id: 'config',       label: 'Config',    icon: '⚙️',  desc: 'Platform settings' },
];

const AdminPage = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [kpis, setKpis] = useState({ users: 0, donations: 0, pending: 0, revenue: 0, expenses: 0 });
  const { refresh } = useRefresh();

  useEffect(() => {
    // Fetch user count
    fetch(`${API_BASE}/vrv-users-getAll`, { headers: { Authorization: `Bearer ${localStorage.getItem('vrvToken')}` } })
      .then(r => r.json()).then(data => {
        const parsed = typeof data.body === 'string' ? JSON.parse(data.body) : data;
        setKpis(prev => ({ ...prev, users: (parsed.users || []).filter(u => u.status === 'ACTIVE').length }));
      }).catch(() => {});
    // Fetch donations
    fetchAllDonations().then(data => {
      const approved = data.filter(d => d.status === 'APPROVED');
      const pending = data.filter(d => d.status === 'PENDING');
      const revenue = approved.reduce((s, d) => s + Number(d.donationAmount || 0), 0);
      setKpis(prev => ({ ...prev, donations: approved.length, pending: pending.length, revenue }));
    }).catch(() => {});
    // Fetch expenses
    fetchAllExpenses().then(data => {
      const total = data.reduce((s, e) => s + Number(e.amount || 0), 0);
      setKpis(prev => ({ ...prev, expenses: total }));
    }).catch(() => {});
  }, [refresh]);

  const balance = kpis.revenue - kpis.expenses;

  const heroKpis = [
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" /></svg>, value: kpis.users || '—', label: 'Active Members', onClick: null },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: kpis.pending || '—', label: 'Pending Review', onClick: () => setActiveTab('transactions') },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, value: kpis.revenue ? `₹${kpis.revenue.toLocaleString()}` : '—', label: 'Total Income', onClick: null },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" /></svg>, value: kpis.expenses ? `₹${kpis.expenses.toLocaleString()}` : '—', label: 'Total Expenses', onClick: null },
    { icon: <svg width="20" height="20" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, value: `₹${balance.toLocaleString()}`, label: 'Balance', onClick: null, color: balance >= 0 ? '#22C55E' : '#EF4444' },
  ];

  return (
    <div style={{ background: T.bg, color: T.ink, minHeight: '100vh' }} className="">
      {/* Hero with KPIs */}
      <section className="vrv-hero">
        <div className="vrv-container">
          <div className="relative z-10">
            <p className="vrv-tag mb-3">Administration</p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>Admin Operations</h1>
            <p className="text-base mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>Manage users, donations, and platform configuration.</p>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 max-w-3xl">
              {heroKpis.map((k, i) => (
                <div key={i} className="vrv-kpi-card" onClick={k.onClick}
                  style={k.onClick ? { cursor: 'pointer' } : {}}>
                  <div className="mb-1.5" style={{ color: 'rgba(255,255,255,0.8)' }}>{k.icon}</div>
                  <p className="text-2xl font-bold" style={{ fontFamily: 'Poppins, sans-serif', color: k.color || 'white' }}>{k.value}</p>
                  <p className="text-[10px] uppercase tracking-wider" style={{ color: 'rgba(255,255,255,0.5)' }}>{k.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="vrv-container py-10" style={{ overflowAnchor: 'none' }}>
        {/* Tab Cards */}
        <div className="grid grid-cols-3 gap-4 mb-10">
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex flex-col items-center gap-2 p-5 rounded-2xl text-center"
                style={{
                  background: active ? T.navy : 'white',
                  border: active ? `2px solid ${T.gold}` : '1.5px solid rgba(0,0,0,0.06)',
                  boxShadow: active ? `0 0 0 3px rgba(212,175,55,0.12), 0 8px 30px rgba(22,33,62,0.18)` : '0 4px 16px rgba(0,0,0,0.05)',
                }}>
                <span className="text-3xl">{tab.icon}</span>
                <span className="font-bold text-sm" style={{ fontFamily: 'Poppins, sans-serif', color: active ? 'white' : T.navy }}>{tab.label}</span>
                <span className="text-xs hidden sm:block" style={{ color: active ? 'rgba(255,255,255,0.6)' : T.muted }}>{tab.desc}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        <div>
          {activeTab === 'users'        && <AdminUsersTab />}
          {activeTab === 'transactions' && <TransactionsPage />}
          {activeTab === 'config'       && <AdminConfigTab />}
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
